// Message-type GROUP tokens for the common results endpoint
// (`{tenant}/healthlink/common/results`, `messageTypes` query param).
//
// The BE now accepts these group names directly and expands each to its
// underlying HL7 message types — so tabs send a single group token instead of
// enumerating every type. Behaviour confirmed by the BE team (2026-06-11):
//   - Groups can be combined, and combined with individual message types.
//   - Unknown tokens are ignored (so "postal" is safe to send today — it
//     simply returns nothing until those messages exist).
//   - Sending NO token defaults to laboratory + radiology + coop/discharges,
//     which is exactly the "All results" view — so that tab sends nothing.
export const MESSAGE_TYPE_GROUPS = {
  laboratory: "laboratory",
  radiology: "radiology",
  coopDischarges: "coop / discharges",
  appointment: "appointment",
  referralResponses: "referral responses",
  other: "other",
  // Not in use yet — BE returns nothing for it today, kept for the future.
  postal: "postal",
};

// Individual HL7 message types, for a view that wants one type rather than a
// whole group. The endpoint resolves a non-group token through the message
// type's DISPLAY NAME (`HealthlinkMessageType.fromDisplayNameIgnoreErrors`),
// not its numeric id — so "Discharge Summary" is the token and "5" matches
// nothing. A row's own `resultType` is that same display name.
export const MESSAGE_TYPES = {
  dischargeSummary: "Discharge Summary",
};

// Tab value → group tokens for the common results endpoint. "all-results" is
// intentionally absent: it sends no token, so the BE applies its default
// (laboratory + radiology + coop/discharges). Laboratory and Radiology have
// their own dedicated endpoints and are absent too.
export const RESULTS_TAB_MESSAGE_TYPES = {
  appointment: [MESSAGE_TYPE_GROUPS.appointment],
  "referral-responses": [MESSAGE_TYPE_GROUPS.referralResponses],
  "coop-discharges": [MESSAGE_TYPE_GROUPS.coopDischarges],
  "postal-correspondence": [MESSAGE_TYPE_GROUPS.postal],
  other: [MESSAGE_TYPE_GROUPS.other],
};

// Tabs backed by the common results endpoint (matched + unmatched). Laboratory
// and Radiology use their own dedicated endpoints and are intentionally absent.
export const COMMON_RESULTS_TABS = [
  "all-results",
  "appointment",
  "referral-responses",
  "coop-discharges",
  "postal-correspondence",
  "other",
];

// Accepts a raw tab value or one carrying the "-unmatched" suffix.
export const isCommonResultsTab = (tab) =>
  COMMON_RESULTS_TABS.includes((tab || "").replace(/-unmatched$/, ""));

// Patient Results "Filters" options. The patient view runs everything through
// the common endpoint (scoped by patientId), so Laboratory/Radiology are
// expressed as group tokens too. "All Results" sends no token — the BE default
// (laboratory + radiology + coop/discharges) is the All Results view.
export const PATIENT_RESULTS_TYPE_FILTERS = [
  { value: "all-results", label: "All Results", messageTypes: null },
  {
    value: "laboratory",
    label: "Laboratory",
    messageTypes: [MESSAGE_TYPE_GROUPS.laboratory],
  },
  {
    value: "radiology",
    label: "Radiology",
    messageTypes: [MESSAGE_TYPE_GROUPS.radiology],
  },
  {
    value: "appointment",
    label: "Appointment",
    messageTypes: [MESSAGE_TYPE_GROUPS.appointment],
  },
  {
    value: "referral-responses",
    label: "Referral responses",
    messageTypes: [MESSAGE_TYPE_GROUPS.referralResponses],
  },
  {
    value: "coop-discharges",
    label: "COOP / Discharges",
    messageTypes: [MESSAGE_TYPE_GROUPS.coopDischarges],
  },
  {
    value: "postal-correspondence",
    label: "Postal correspondence",
    messageTypes: [MESSAGE_TYPE_GROUPS.postal],
  },
  { value: "other", label: "Other", messageTypes: [MESSAGE_TYPE_GROUPS.other] },
];
